import { useState } from "react";
import { X, ChevronRight, ChevronLeft } from "lucide-react";

const TOUR_STEPS = [
  {
    title: "Welcome to Partner HQ",
    body: "This is the control center for the LawOS Channel Partner Commission Engine — a 5-level partner network with automated commission cascades, TDS deductions, and Razorpay payout integration.",
    nav: null,
  },
  {
    title: "Partner Network",
    body: "Add partners at any hierarchy level: National → Regional → Zonal → Sales Manager → Sales Partner. Each partner is linked to their parent, and commissions cascade upward automatically on every subscription or wallet recharge.",
    nav: "/sm/hierarchy",
    navLabel: "Go to Partner Network",
  },
  {
    title: "Commission Engine",
    body: "The Commission Ledger records every commission event with a full audit trail — gross amount, net (after GST), TDS deducted, and the cascade split per level. Approve entries here or in bulk from the Statements page.",
    nav: "/sm/commission-ledger",
    navLabel: "Go to Commission Ledger",
  },
  {
    title: "Monthly Statements & Payouts",
    body: "Generate monthly statements for each partner, approve them, and trigger bulk payouts via Razorpay X AutoPay. TDS certificates (Form 16A — Section 194H) are auto-generated per partner per quarter.",
    nav: "/sm/statements",
    navLabel: "Go to Statements",
  },
  {
    title: "CRM & Deal Pipeline",
    body: "Track prospects (law firms, individual lawyers, individuals) through a 7-stage deal pipeline. The privacy-first 'Call' button reveals phone numbers only on explicit action. Closing a deal auto-triggers the commission cascade.",
    nav: "/sm/crm",
    navLabel: "Go to CRM",
  },
  {
    title: "AI Business Guide",
    body: "The AI Guide has 5 hierarchy-specific personas — from Sales Partner coach to National Partner strategist. It loads your real commission and pipeline data and answers business questions. No case files or tenant PII are ever accessed.",
    nav: "/sm/ai-guide",
    navLabel: "Go to AI Guide",
  },
  {
    title: "Partner KYC",
    body: "Verify partner PAN (format validation), Aadhaar (OTP flow), and bank account (penny-drop) before enabling payouts. Currently using a mock provider — swap the verification functions in sm_kyc.py when sandbox credentials are available.",
    nav: "/sm/kyc",
    navLabel: "Go to Partner KYC",
  },
];

export const SM_TOUR_KEY = "sm_tour_v1_done";

export default function SMTour({ onClose }) {
  const [step, setStep] = useState(0);
  const current = TOUR_STEPS[step];
  const total = TOUR_STEPS.length;

  const handleClose = () => {
    localStorage.setItem(SM_TOUR_KEY, "1");
    onClose();
  };

  const handleNext = () => {
    if (step >= total - 1) { handleClose(); return; }
    setStep(s => s + 1);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="sm-tour-overlay">
      <div className="absolute inset-0 bg-black/60" onClick={handleClose} />
      <div className="relative z-10 bg-card border border-border rounded-2xl p-6 max-w-sm w-full mx-4 shadow-2xl">
        {/* Step indicators */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex gap-1">
            {Array.from({ length: total }).map((_, i) => (
              <div key={i}
                className={`h-1 rounded-full transition-all ${i === step ? "w-6 bg-primary" : i < step ? "w-3 bg-primary/40" : "w-2 bg-muted-foreground/20"}`} />
            ))}
          </div>
          <button onClick={handleClose} data-testid="sm-tour-skip"
            className="text-muted-foreground hover:text-foreground transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <h3 className="text-base font-semibold text-foreground mb-2">{current.title}</h3>
        <p className="text-[13px] text-muted-foreground leading-relaxed mb-5">{current.body}</p>

        {/* Nav hint */}
        {current.nav && (
          <a href={current.nav}
            className="block text-center text-[11px] px-3 py-2 bg-muted rounded-lg text-muted-foreground hover:text-primary hover:bg-muted/80 transition-all mb-4">
            {current.navLabel} →
          </a>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between">
          <span className="text-[11px] text-muted-foreground">{step + 1} of {total}</span>
          <div className="flex gap-2">
            {step > 0 && (
              <button onClick={() => setStep(s => s - 1)} data-testid="sm-tour-prev"
                className="flex items-center gap-1 px-3 py-2 border border-border rounded-lg text-[12px] text-muted-foreground hover:text-foreground hover:bg-muted transition-all">
                <ChevronLeft className="w-3.5 h-3.5" />Back
              </button>
            )}
            <button onClick={handleNext} data-testid="sm-tour-next"
              className="flex items-center gap-1.5 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-[12px] font-medium hover:opacity-90 transition-opacity">
              {step === total - 1 ? "Get Started" : "Next"}
              {step < total - 1 && <ChevronRight className="w-3.5 h-3.5" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
